import React, { useEffect } from "react";
import { BasicModal } from "../../BasicModal/BasicModal";
import { ReactComponent as InvoiceIcon } from "../../../SVGcomponents/InvoiceIcon.svg";
import BasicInput from "../../../Inputs/BasicInput";
import BasicButton, {
  RESPONSIVE_BREAKPOINTS,
} from "../../../Buttons/BasicButton";
import styles from "../styles.module.scss";
import { useForm } from "react-hook-form";
import axios from "../../../../api/axios";
import { triggerGlobalNotifications } from "../../../../context/ErrorBridge";
import { useNotification } from "src/context/NotificationContext";

type Props = {
  closeModal: () => void;
  isOpened: boolean;
};

interface FormInputs {
  amount: string;
  email: string;
  bankName: string;
  accountNumber: string;
  routingNumber: string;
  accountHoldersName: string;
}

const LeftIcon = () => {
  return <span className={styles["left-icon"]}>$</span>;
};

// Same minimum as the card and crypto top-ups.
const MINIMUM_PAYMENT_AMOUNT = 100;
const MINIMUM_ERROR = `Minimum payment amount - $${MINIMUM_PAYMENT_AMOUNT}`;

export const AddFundInvoiceModal = ({ isOpened, closeModal }: Props) => {
  const { register, handleSubmit, formState, reset } = useForm<FormInputs>();
  const { callNotification } = useNotification();

  return (
    <BasicModal
      sx={{ width: "482px" }}
      isOpened={isOpened}
      closeModal={closeModal}
    >
      <>
        <div className={styles["title-box"]}>
          <InvoiceIcon style={{ width: "21px", height: "21px" }} />
          <h3 className={styles["title"]}>Add funds</h3>
          <span className={styles["method-type"]}>Invoice</span>
        </div>
        <form
          onSubmit={handleSubmit(async (data) => {
            try {
              await axios.post("/payments/createPayment/invoice", data);
              callNotification();
              reset();
              closeModal();
            } catch (error) {
              triggerGlobalNotifications({
                type: "error",
                data: {
                  message:
                    "Failed to create invoice payment request. Please try again.",
                  placement: "top",
                },
              });
            }
          })}
        >
          <div className={styles["input-box"]}>
            <label htmlFor="amount">Amount</label>
            <BasicInput
              type="text"
              registerData={register}
              name="amount"
              placeholder={"0.00"}
              error={!!formState.errors.amount?.message}
              cls={styles["modalInputWithIcon"]}
              LeftIcon={LeftIcon}
              registerOptions={{
                validate: (value: string) =>
                  (/^\d*\.?\d*$/.test(value) &&
                    Number(value) >= MINIMUM_PAYMENT_AMOUNT) ||
                  MINIMUM_ERROR,
              }}
            />
            {formState.errors.amount?.type === "validate" && (
              <div className={styles.minimumError}>{MINIMUM_ERROR}</div>
            )}
          </div>
          <div className={styles["input-box"]}>
            <label htmlFor="email">Email</label>
            <BasicInput
              cls={styles["modalInput"]}
              registerData={register}
              type="text"
              name="email"
              placeholder={"Enter email"}
              error={!!formState.errors.email?.message}
            />
          </div>
          <div className={styles["input-box"]}>
            <label htmlFor="bankName">Bank Name</label>
            <BasicInput
              cls={styles["modalInput"]}
              name="bankName"
              type="text"
              registerData={register}
              placeholder={"Enter bank name"}
              error={!!formState.errors.bankName?.message}
            />
          </div>
          <div className={styles["input-box"]}>
            <label htmlFor="holdersName">Account holder's name</label>
            <BasicInput
              registerData={register}
              name="accountHoldersName"
              type="text"
              placeholder={"Enter account holder's name"}
              cls={styles["modalInput"]}
              error={!!formState.errors.accountHoldersName?.message}
            />
          </div>
          <div className={styles["input-box"]}>
            <label htmlFor="accountNumber">Account number</label>
            <BasicInput
              registerData={register}
              type="text"
              name="accountNumber"
              placeholder={"Enter account number"}
              cls={styles["modalInput"]}
              error={!!formState.errors.accountNumber?.message}
            />
          </div>
          <div className={styles["input-box"]}>
            <label htmlFor="aba">Routing number (ABA)</label>
            <BasicInput
              registerData={register}
              name="routingNumber"
              type="text"
              placeholder={"Enter routing name"}
              cls={styles["modalInput"]}
              error={!!formState.errors.routingNumber?.message}
            />
          </div>

          <div className={styles["button-box"]}>
            <BasicButton
              type="button"
              size="large"
              responsiveBreakpoints={{
                mac13: {
                  mediaQuery: RESPONSIVE_BREAKPOINTS.MAC_13,
                  customSize: {
                    height: 40,
                  },
                },
              }}
              onClick={closeModal}
              title="Cancel"
              variant="secondary"
              cls={styles.cancelButton}
            />
            <BasicButton
              type="submit"
              size="large"
              responsiveBreakpoints={{
                mac13: {
                  mediaQuery: RESPONSIVE_BREAKPOINTS.MAC_13,
                  customSize: {
                    height: 40,
                  },
                },
              }}
              disabled={!formState.isValid}
              title="Confirm"
              variant="primary"
              cls={styles.confirmButton}
            />
          </div>
        </form>
      </>
    </BasicModal>
  );
};
